import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Users } from "lucide-react";
import { socialApi } from "@/api/social";
import { useSession } from "@/context/SessionContext";
import { UserTile, ProfileCard } from "@/components/social/SocialDirectory";

export default function FollowersList({ onBack, onMessage }) {
  const { token } = useSession();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await socialApi.followers();
      setRows(d.followers || []);
    } catch {
      toast.error("Could not load followers");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mutate = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      if (action === "request") await socialApi.request(selected.id);
      else if (action === "unfriend") await socialApi.unfriend(selected.id);
      else if (action === "block") await socialApi.blockUser(selected.id);
      else if (action === "unblock") await socialApi.unblock(selected.id);
      else if (action === "follow") {
        const r = await socialApi.follow(selected.id);
        toast.success(r.active ? "Following" : "Unfollowed");
        setSelected((s) => ({ ...s, following: r.active }));
        setBusy(false);
        load();
        return;
      } else await socialApi.respond(selected.id, action);
      toast.success(
        action === "accept" ? "Friend added" :
        action === "request" ? "Request sent" :
        action === "block" ? "Blocked" :
        action === "unblock" ? "Unblocked" : "Done"
      );
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-cream relative" data-testid="followers-list">
      {selected && (
        <ProfileCard person={selected} busy={busy} token={token} onClose={() => setSelected(null)} onMutate={mutate} onMessage={onMessage} />
      )}
      <header className="shrink-0 px-4 pt-3 pb-2 flex items-center gap-3">
        <button onClick={onBack} data-testid="followers-back-btn" className="w-9 h-9 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={16} strokeWidth={2.4} className="text-charcoal" />
        </button>
        <div className="leading-none flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Community</p>
          <h1 className="font-serif text-2xl text-charcoal flex items-center gap-2"><Users size={18} className="text-gold" /> Followers · {rows.length}</h1>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-2">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 size={26} className="text-gold animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="text-center font-mono text-xs text-charcoal/40 mt-10" data-testid="followers-empty">No followers yet — share your reels to grow your audience.</p>
        ) : (
          rows.map((p) => <UserTile key={p.id} person={p} onClick={setSelected} token={token} />)
        )}
      </div>
    </div>
  );
}
